// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { getUsers, inviteUser, updateUser, deleteUser } from '../services/userService';
import type { UserResponse, UserInvite, UserUpdate } from '../types';
import type { UserRole, UserStatus } from '../types';
import './UsersPage.css';

// ── Local interfaces ──

interface Site {
  id: string;
  name: string;
}

type StagedStatus = 'ready' | 'adding' | 'added' | 'failed';

interface StagedUser {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  _status: StagedStatus;
  _error: string | null;
  _id: string;
}

interface ParsedSpreadsheetRow {
  [key: string]: string;
}

interface ParseSpreadsheetResult {
  users: StagedUser[];
  errors: string[];
}

interface SubmissionSummary {
  success: number;
  total: number;
}

/** Base form data (invite mode) */
interface BaseFormData {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  site_ids: string[];
}

/** Edit-mode form data extends base with optional password and status */
interface EditFormData extends BaseFormData {
  password?: string;
  status?: string;
}

type FormData = BaseFormData | EditFormData;

type ModalMode = 'invite' | 'edit';

type MappedFieldName = 'first_name' | 'last_name' | 'email' | 'role';

const VALID_ROLES: readonly UserRole[] = [
  'admin',
  'operations_manager',
  'operator',
  'executive',
] as const;

/**
 * Map a raw column header to a known field name.
 * Supports common variations (case-insensitive, underscores, spaces).
 */
function mapColumnName(raw: string): MappedFieldName | null {
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (['firstname', 'first'].includes(key)) return 'first_name';
  if (['lastname', 'last'].includes(key)) return 'last_name';
  if (key === 'email') return 'email';
  if (key === 'role') return 'role';
  return null;
}

/**
 * Parse a spreadsheet (CSV, XLSX, XLS) File into an array of user objects.
 * Returns { users, errors } where errors lists validation issues.
 */
function parseSpreadsheet(file: File): Promise<ParseSpreadsheetResult> {
  return new Promise<ParseSpreadsheetResult>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (rows.length === 0) {
          resolve({ users: [], errors: ['Spreadsheet is empty or has no data rows.'] });
          return;
        }

        // Map column headers
        const rawHeaders = Object.keys(rows[0]);
        const headerMap: Record<string, MappedFieldName> = {};
        for (const h of rawHeaders) {
          const mapped = mapColumnName(h);
          if (mapped) headerMap[h] = mapped;
        }

        const requiredFields: MappedFieldName[] = ['email', 'first_name', 'last_name'];
        const mappedFields = Object.values(headerMap);
        const missing = requiredFields.filter(f => !mappedFields.includes(f));
        if (missing.length > 0) {
          resolve({
            users: [],
            errors: [
              `Missing required columns: ${missing.join(', ')}. Found columns: ${rawHeaders.join(', ')}`,
            ],
          });
          return;
        }

        const users: StagedUser[] = [];
        const errors: string[] = [];

        rows.forEach((row, idx) => {
          const user: ParsedSpreadsheetRow = {};
          for (const [rawKey, mappedKey] of Object.entries(headerMap)) {
            user[mappedKey] = String(row[rawKey] || '').trim();
          }

          // Validation
          const rowNum = idx + 2; // 1-indexed header + 1-indexed row
          if (!user.email) {
            errors.push(`Row ${rowNum}: Missing email`);
            return;
          }
          if (!user.first_name) {
            errors.push(`Row ${rowNum}: Missing first name`);
            return;
          }
          if (!user.last_name) {
            errors.push(`Row ${rowNum}: Missing last name`);
            return;
          }

          // Normalise role
          let role: string;
          if (user.role) {
            const normalised = user.role.toLowerCase().replace(/[\s-]+/g, '_');
            role = (VALID_ROLES as readonly string[]).includes(normalised)
              ? normalised
              : 'operator';
          } else {
            role = 'operator';
          }

          users.push({
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            role,
            _status: 'ready',
            _error: null,
            _id: `staged-${Date.now()}-${idx}`,
          });
        });

        resolve({ users, errors });
      } catch (err) {
        reject(new Error(`Failed to parse file: ${(err as Error).message}`));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

function UsersPage(): React.JSX.Element {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal states
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<ModalMode>('invite');
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'operator',
    site_ids: [],
  });

  // Bulk upload states
  const [stagedUsers, setStagedUsers] = useState<StagedUser[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionSummary, setSubmissionSummary] = useState<SubmissionSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchUsers();
    fetchSites();
  }, []);

  const fetchUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async (): Promise<void> => {
    try {
      setSites([]);
    } catch (err) {
      console.error('Failed to fetch sites:', err);
    }
  };

  // ── Single-user invite / edit / delete (existing) ──

  const handleInviteUser = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const inviteData: UserInvite = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role as UserRole,
        site_ids: formData.site_ids,
      };
      await inviteUser(inviteData);
      await fetchUsers();
      setShowModal(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'operator',
        site_ids: [],
      });
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const handleEditUser = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      const editData = formData as EditFormData;
      const updateData: UserUpdate = {
        first_name: editData.first_name,
        last_name: editData.last_name,
        role: editData.role as UserRole,
        status: editData.status as UserStatus,
      };
      if (editData.password) {
        updateData.password = editData.password;
      }

      await updateUser(selectedUser.id, updateData);

      await fetchUsers();
      setShowModal(false);
      setSelectedUser(null);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'operator',
        site_ids: [],
      });
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const handleDeleteUser = async (userId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(userId);
      await fetchUsers();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const openInviteModal = (): void => {
    setModalMode('invite');
    setSelectedUser(null);
    setFormData({ email: '', first_name: '', last_name: '', role: 'operator', site_ids: [] });
    setShowModal(true);
  };

  const openEditModal = (user: UserResponse): void => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      status: user.status,
      password: '',
      site_ids: user.site_ids || [],
    });
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({ email: '', first_name: '', last_name: '', role: 'operator', site_ids: [] });
  };

  // ── Bulk upload handlers ──

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseErrors([]);
    setSubmissionSummary(null);

    try {
      const { users: parsed, errors } = await parseSpreadsheet(file);
      setStagedUsers(parsed);
      setParseErrors(errors);
    } catch (err) {
      setParseErrors([(err as Error).message]);
      setStagedUsers([]);
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updateStagedUser = (id: string, field: string, value: string): void => {
    setStagedUsers(prev => prev.map(u => (u._id === id ? { ...u, [field]: value } : u)));
  };

  const removeStagedUser = (id: string): void => {
    setStagedUsers(prev => prev.filter(u => u._id !== id));
  };

  const clearStaged = (): void => {
    setStagedUsers([]);
    setParseErrors([]);
    setSubmissionSummary(null);
  };

  const handleBulkSubmit = async (): Promise<void> => {
    const toSubmit = stagedUsers.filter(u => u._status === 'ready' || u._status === 'failed');
    if (toSubmit.length === 0) return;

    setIsSubmitting(true);
    setSubmissionSummary(null);
    let successCount = 0;

    for (const staged of toSubmit) {
      // Mark as adding
      setStagedUsers(prev =>
        prev.map(u =>
          u._id === staged._id ? { ...u, _status: 'adding' as const, _error: null } : u
        )
      );

      try {
        await inviteUser({
          email: staged.email,
          first_name: staged.first_name,
          last_name: staged.last_name,
          role: staged.role as UserRole,
        });
        setStagedUsers(prev =>
          prev.map(u =>
            u._id === staged._id ? { ...u, _status: 'added' as const, _error: null } : u
          )
        );
        successCount++;
      } catch (err) {
        setStagedUsers(prev =>
          prev.map(u =>
            u._id === staged._id
              ? { ...u, _status: 'failed' as const, _error: (err as Error).message }
              : u
          )
        );
      }
    }

    setIsSubmitting(false);
    setSubmissionSummary({ success: successCount, total: toSubmit.length });

    // Refresh the existing users list
    await fetchUsers();
  };

  // ── Helpers ──

  const getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'badge-admin';
      case 'operations_manager':
        return 'badge-ops-manager';
      case 'operator':
        return 'badge-operator';
      case 'executive':
        return 'badge-executive';
      default:
        return '';
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active':
        return 'badge-active';
      case 'invited':
        return 'badge-invited';
      case 'pending':
        return 'badge-pending';
      default:
        return '';
    }
  };

  const formatRole = (role: string): string => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Filter logic
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="users-page">
        <div className="users-container">
          <div className="loading-state">Loading users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-page">
        <div className="users-container">
          <div className="error-state">Error: {error}</div>
        </div>
      </div>
    );
  }

  const uniqueRoles = Array.from(new Set(users.map(u => u.role)));
  const uniqueStatuses = Array.from(new Set(users.map(u => u.status)));
  const pendingCount = stagedUsers.filter(
    u => u._status === 'ready' || u._status === 'failed'
  ).length;

  return (
    <div className="users-page">
      <div className="users-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">User Management</h1>
            <p className="page-subtitle">Invite and manage users in your organization</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button className="btn-create" onClick={openInviteModal}>
              + Invite User
            </button>
          </div>
        </div>

        {/* ── Bulk Upload Section ── */}
        <div className="bulk-upload-section">
          <div className="bulk-upload-header">
            <h2 className="bulk-upload-title">Bulk Add Users</h2>
            <p className="bulk-upload-subtitle">
              Upload a CSV or Excel file with columns: First Name, Last Name, Email, Role
            </p>
          </div>

          <div className="upload-area">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              disabled={isSubmitting}
              className="file-input"
              id="spreadsheet-upload"
            />
            <label
              htmlFor="spreadsheet-upload"
              className={`upload-label ${isSubmitting ? 'disabled' : ''}`}
            >
              <span className="upload-icon">+</span>
              <span className="upload-text">Choose a spreadsheet file</span>
              <span className="upload-hint">.csv, .xlsx, .xls</span>
            </label>
          </div>

          {/* Parse errors */}
          {parseErrors.length > 0 && (
            <div className="parse-errors">
              {parseErrors.map((err, i) => (
                <div key={i} className="parse-error-item">
                  {err}
                </div>
              ))}
            </div>
          )}

          {/* Submission summary */}
          {submissionSummary && (
            <div
              className={`submission-summary ${submissionSummary.success === submissionSummary.total ? 'summary-success' : 'summary-partial'}`}
            >
              {submissionSummary.success} of {submissionSummary.total} users added successfully.
              {submissionSummary.success < submissionSummary.total &&
                ' Failed users can be retried.'}
            </div>
          )}

          {/* Staging table */}
          {stagedUsers.length > 0 && (
            <div className="staging-section">
              <div className="staging-header">
                <span className="staging-count">
                  {stagedUsers.length} user{stagedUsers.length !== 1 ? 's' : ''} loaded
                </span>
                <div className="staging-actions">
                  <button
                    className="btn-bulk-submit"
                    onClick={handleBulkSubmit}
                    disabled={isSubmitting || pendingCount === 0}
                  >
                    {isSubmitting
                      ? 'Adding Users...'
                      : `Add ${pendingCount} User${pendingCount !== 1 ? 's' : ''}`}
                  </button>
                  <button
                    className="btn-clear-staged"
                    onClick={clearStaged}
                    disabled={isSubmitting}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="staging-table-container">
                <table className="staging-table">
                  <thead>
                    <tr>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stagedUsers.map(staged => (
                      <tr key={staged._id} className={`staged-row staged-${staged._status}`}>
                        <td>
                          <input
                            type="text"
                            className="staged-input"
                            value={staged.first_name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateStagedUser(staged._id, 'first_name', e.target.value)
                            }
                            disabled={isSubmitting || staged._status === 'added'}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="staged-input"
                            value={staged.last_name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateStagedUser(staged._id, 'last_name', e.target.value)
                            }
                            disabled={isSubmitting || staged._status === 'added'}
                          />
                        </td>
                        <td>
                          <input
                            type="email"
                            className="staged-input"
                            value={staged.email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateStagedUser(staged._id, 'email', e.target.value)
                            }
                            disabled={isSubmitting || staged._status === 'added'}
                          />
                        </td>
                        <td>
                          <select
                            className="staged-select"
                            value={staged.role}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              updateStagedUser(staged._id, 'role', e.target.value)
                            }
                            disabled={isSubmitting || staged._status === 'added'}
                          >
                            {VALID_ROLES.map(r => (
                              <option key={r} value={r}>
                                {formatRole(r)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <span className={`staged-status staged-status-${staged._status}`}>
                            {staged._status === 'ready' && 'Ready'}
                            {staged._status === 'adding' && 'Adding...'}
                            {staged._status === 'added' && 'Added'}
                            {staged._status === 'failed' && `Failed: ${staged._error}`}
                          </span>
                        </td>
                        <td>
                          {staged._status !== 'added' && (
                            <button
                              className="btn-action btn-delete"
                              onClick={() => removeStagedUser(staged._id)}
                              disabled={isSubmitting}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── Existing Users Section ── */}

        {/* Filters Section */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Role:</label>
            <select
              value={roleFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>
                  {formatRole(role)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select
              value={statusFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setStatusFilter(e.target.value)
              }
              className="filter-select"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-results">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>

        {/* Users Table */}
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Sites</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                return (
                  <tr key={user.id}>
                    <td className="username-cell">
                      {user.first_name} {user.last_name}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {formatRole(user.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      {user.site_ids && user.site_ids.length > 0
                        ? `${user.site_ids.length} assigned`
                        : 'None'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-action btn-edit" onClick={() => openEditModal(user)}>
                          Edit
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="empty-state">No users found matching the current filters.</div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-content"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{modalMode === 'invite' ? 'Invite New User' : 'Edit User'}</h2>
                <button className="modal-close" onClick={closeModal}>
                  ×
                </button>
              </div>

              <form onSubmit={modalMode === 'invite' ? handleInviteUser : handleEditUser}>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    placeholder="user@example.com"
                    disabled={modalMode === 'edit'}
                  />
                </div>

                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    required
                    placeholder="Enter first name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    required
                    placeholder="Enter last name"
                  />
                </div>

                {modalMode === 'edit' && (
                  <div className="form-group">
                    <label>
                      Password <span className="optional-label">(leave blank to keep current)</span>
                    </label>
                    <input
                      type="password"
                      value={(formData as EditFormData).password || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="Leave blank to keep current"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                  >
                    <option value="operator">Operator</option>
                    <option value="operations_manager">Operations Manager</option>
                    <option value="admin">Admin</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                {modalMode === 'edit' && (
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={(formData as EditFormData).status}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      required
                    >
                      <option value="invited">Invited</option>
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Site Assignments</label>
                  <div
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px',
                      minHeight: '80px',
                      maxHeight: '150px',
                      overflowY: 'auto',
                    }}
                  >
                    {sites.length === 0 ? (
                      <p style={{ color: '#999', fontSize: '14px', margin: '8px' }}>
                        No sites available
                      </p>
                    ) : (
                      sites.map(site => (
                        <label key={site.id} style={{ display: 'block', padding: '4px 0' }}>
                          <input
                            type="checkbox"
                            checked={formData.site_ids.includes(site.id)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const newIds = e.target.checked
                                ? [...formData.site_ids, site.id]
                                : formData.site_ids.filter(id => id !== site.id);
                              setFormData({ ...formData, site_ids: newIds });
                            }}
                          />{' '}
                          {site.name}
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    {modalMode === 'invite' ? 'Send Invitation' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsersPage;
